import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useMarkNotificationReadForEntity } from "../../context/NotificationContext";
import { Header } from "../../components/vc/Header";
import { EnquiryCard } from "../../components/vc/EnquiryCard";
import { EmptyState, Section, SkeletonList } from "../../components/vc/layout";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { Input } from "../../components/ui/input";
import {
  ArrowLeft, FileText, Send, MessageCircle, Building2, Plus, Pencil, Clock,
} from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { ImageViewer } from "../../components/vc/ImageViewer";
import { QuoteImagePane } from "../../components/vc/QuoteImagePane";
import { computeRetention } from "../../lib/retention";

/**
 * Inbound enquiries — persisted via /api/inbound-enquiries (real backend).
 * Receiving business sees ONLY items assigned to them.
 */
export function VCIncomingEnquiries() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("/inbound-enquiries").then((r) => {
      setEnquiries((r.data || []).map((e) => {
        const items = e.items || [];
        const summary = items.slice(0, 2).map((it) => it.product).join(", ")
          + (items.length > 2 ? `, +${items.length - 2} more` : "");
        const anyQuoted = items.some((it) => (it.quotes || []).length > 0);
        // Phase 4 #2 — once every item assigned to this supplier has been
        // converted into an order, the quotation is locked/read-only; surface
        // that on the list before the supplier even opens it.
        const allConverted = items.length > 0 && items.every((it) => it.order_id || it.ordered_supplier_id);
        return {
          id: e.id,
          business_name: e.buyer_name || "Buyer",
          enquiry_id_label: (e.id || "").slice(0, 6).toUpperCase(),
          date: e.created_at,
          status: allConverted ? "converted" : (e.status === "quoted" || anyQuoted ? "responded" : "pending"),
          product_summary: `${items.length} item${items.length === 1 ? "" : "s"} · ${summary}`,
          created_at: e.created_at,
          last_response_at: e.last_response_at,
        };
      }));
    }).catch(() => setEnquiries([])).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-28 space-y-4">
        <button onClick={() => navigate("/enquiries")} data-testid="incoming-enq-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
          <ArrowLeft className="w-4 h-4" /> Back to enquiries
        </button>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">Incoming Enquiries</h2>
          <p className="text-sm text-slate-500 mt-0.5">Send quotations to your buyers</p>
        </div>

        <Section title="Awaiting your quote" subtitle={`${enquiries.length} received`} testid="incoming-list">
          {loading ? <SkeletonList /> : enquiries.length === 0 ? (
            <EmptyState Icon={FileText} title="No incoming enquiries" subtitle="Buyers who send you enquiries will appear here." />
          ) : (
            <div className="space-y-2">
              {enquiries.map((e) => (
                <EnquiryCard key={e.id} enquiry={e} to={`/enquiries/incoming/${e.id}`} />
              ))}
            </div>
          )}
        </Section>
      </div>
    </>
  );
}

export default function VCSupplierQuotation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [enq, setEnq] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // #3 — viewing this incoming enquiry marks its "new enquiry" notification read.
  useMarkNotificationReadForEntity(id);

  useEffect(() => {
    apiClient.get(`/inbound-enquiries/${id}`)
      .then((r) => {
        setEnq(r.data);
        setRows((r.data.items || []).map((it) => {
          // If this supplier has already quoted, pre-fill the fields so the user
          // sees their current values and can revise them.
          const myQuote = (it.quotes || []).find((q) => q && q.supplier_id) || {};
          const qty = Number(it.quantity || 0);
          const derivedUnit = myQuote.unit_price != null
            ? Number(myQuote.unit_price)
            : (myQuote.price && qty ? Number(myQuote.price) / qty : "");
          const currentProduct = it.product || "";
          return {
            index: it.index,
            product: currentProduct,
            // Image-only enquiries leave `product` empty — the supplier can
            // name it here OR provide a serial number that maps to the
            // buyer's image. `product_missing` freezes the "needs identifier"
            // state even after typing so both inputs stay visible.
            product_missing: !currentProduct.trim(),
            product_input: "",
            // Serial number pulled from the responder's previous quote (if
            // any) so revising shows the value they submitted last time.
            serial_input: myQuote.serial_no || "",
            quantity: it.quantity,
            unit: it.unit,
            unit_price: derivedUnit === "" ? "" : String(derivedUnit),
            // Discount is expressed as a percentage (0-100). Blank by
            // default (no "0" shown) — only a genuinely saved value (which
            // could itself be 0) is pre-filled.
            discount: myQuote.discount != null ? String(myQuote.discount) : "",
            note: myQuote.note || "",
            already_quoted: !!myQuote.supplier_id,
            locked: !!(it.order_id || it.ordered_supplier_id),
          };
        }));
      })
      .catch(() => {
        toast.error("Enquiry not found");
        navigate("/enquiries/incoming");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const totals = useMemo(() => {
    let sub = 0;
    rows.forEach((r) => {
      const unit = Number(r.unit_price || 0);
      const discPct = Math.max(0, Math.min(100, Number(r.discount || 0)));
      const qty = Number(r.quantity || 0);
      const effectiveUnit = unit * (1 - discPct / 100);
      sub += Math.max(0, effectiveUnit) * qty;
    });
    return { sub, grand: sub };
  }, [rows]);

  const update = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  // #9 — Image enquiries: responder can add extra virtual quote rows.
  // Fresh index = max(existing indices) + 1. The backend appends these as
  // new items on submit.
  const addImageRow = () => {
    setRows((r) => {
      const nextIdx = r.reduce((m, x) => Math.max(m, Number(x.index || 0)), -1) + 1;
      return [
        ...r,
        {
          index: nextIdx,
          product: "",
          product_missing: true,     // treats it as a "needs identifier" row
          product_input: "",
          serial_input: "",
          quantity: 1,
          unit: "pcs",
          unit_price: "",
          discount: "",
          note: "",
          already_quoted: false,
          locked: false,
          added_by_responder: true,
        },
      ];
    });
  };

  const removeImageRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const deleteMyQuotes = async () => {
    if (!window.confirm("Are you sure you want to delete this quotation?")) return;
    try {
      await apiClient.delete(`/inbound-enquiries/${id}/quote`);
      toast.success("Quotation deleted");
      navigate("/enquiries/incoming");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  const send = async (viaWA = false) => {
    const filled = rows.filter((r) => Number(r.unit_price) > 0 && !r.locked);
    if (filled.length === 0) {
      toast.error("Enter a unit price for at least one editable item");
      return;
    }
    // Image-only enquiries: require either Product Name OR Serial No. on
    // every row the supplier is quoting for. Product name alone is no
    // longer mandatory — a serial number that maps to the buyer's image
    // is also accepted.
    const missingIdent = filled.find(
      (r) => r.product_missing
        && !(r.product_input || "").trim()
        && !(r.serial_input || "").trim(),
    );
    if (missingIdent) {
      toast.error("Enter Product Name or Serial No. for each quoted item");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post(`/inbound-enquiries/${id}/quote`, {
        item_quotes: filled.map((r) => {
          const unit = Number(r.unit_price || 0);
          const discPct = Math.max(0, Math.min(100, Number(r.discount || 0)));
          const qty = Number(r.quantity || 0);
          const total = Math.max(0, unit * (1 - discPct / 100)) * qty;
          const payloadRow = {
            item_index: r.index,
            price: total,
            unit_price: unit,
            discount: discPct,
            note: (r.note || "").trim(),
          };
          if (r.product_missing && (r.product_input || "").trim()) {
            payloadRow.product = r.product_input.trim();
          } else if (!r.product_missing && (r.product_input || "").trim() && r.product_input.trim() !== r.product) {
            // #6 — Responder edited the description via the pencil icon; send
            // the override so it's saved onto their quote (buyer's original
            // enquiry item is not modified).
            payloadRow.product = r.product_input.trim();
          }
          if ((r.serial_input || "").trim()) {
            payloadRow.serial_no = r.serial_input.trim();
          }
          return payloadRow;
        }),
      });
      if (viaWA) {
        const lines = filled.map((r, i) => {
          const unit = Number(r.unit_price || 0);
          const discPct = Math.max(0, Math.min(100, Number(r.discount || 0)));
          const qty = Number(r.quantity || 0);
          const total = Math.max(0, unit * (1 - discPct / 100)) * qty;
          const discPart = discPct > 0 ? ` (− ${discPct}%)` : "";
          const typedName = (r.product_input || "").trim();
          const serial = (r.serial_input || "").trim();
          const productLabel = r.product_missing
            ? (typedName || (serial ? `#${serial}` : ""))
            : (typedName || r.product);
          return `${i + 1}. ${productLabel || "Item"} × ${qty} ${r.unit} @ ₹${unit.toFixed(2)}${discPart} = ₹${total.toFixed(2)}`;
        }).join("\n");
        const msg = `Quotation from KonexoCRM\n\n${lines}\n\nGrand Total: ₹${totals.grand.toFixed(2)}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
      }
      const anyRevise = filled.some((r) => r.already_quoted);
      toast.success(anyRevise ? "Quotation revised" : "Quotation sent");
      navigate("/enquiries/incoming");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !enq) {
    return (
      <>
        <Header />
        <div className="px-4 py-8 text-center text-slate-500">Loading...</div>
      </>
    );
  }

  const hasBuyerImages = Array.isArray(enq.images) && enq.images.length > 0;

  const allLocked = rows.length > 0 && rows.every((r) => r.locked);

  const retentionPill = (() => {
    const retention = computeRetention({
      status: enq.status,
      created_at: enq.created_at,
      last_response_at: enq.last_response_at,
    });
    if (!retention || (enq.status || "").toLowerCase() === "closed" || allLocked) return null;
    const tone = retention.daysLeft <= 7 ? "text-amber-700 bg-amber-50" : "text-slate-600 bg-slate-100";
    return (
      <div
        data-testid="quotation-retention"
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold w-fit ${tone}`}
      >
        <Clock className="w-3 h-3" strokeWidth={2.4} />
        Auto deletion in: {retention.daysLeft} {retention.daysLeft === 1 ? "Day" : "Days"}
      </div>
    );
  })();

  const businessCard = (
    <div data-testid="quotation-business-card" className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3 shadow-sm">
      <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
        <Building2 className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-slate-900">{enq.buyer_name || "Buyer"}</div>
        <div className="text-xs text-slate-500 font-mono">#{(enq.id || "").slice(0, 6).toUpperCase()}</div>
      </div>
      <StatusBadge status={allLocked ? "converted" : (enq.status || "pending")} testid="quotation-status-badge" />
    </div>
  );

  const backButton = (
    <button onClick={() => navigate(-1)} data-testid="quotation-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );

  const itemsBlock = (
    <div className="space-y-2" data-testid="quotation-items">
          {rows.map((row, i) => {
            const unit = Number(row.unit_price || 0);
            const discPct = Math.max(0, Math.min(100, Number(row.discount || 0)));
            const qty = Number(row.quantity || 0);
            const lineTotal = Math.max(0, unit * (1 - discPct / 100)) * qty;
            return (
              <div
                key={row.index}
                data-testid={`quote-row-${row.index}`}
                data-locked={row.locked ? "true" : "false"}
                className={`border rounded-2xl p-3 space-y-2 shadow-sm ${row.locked ? "bg-slate-50 border-slate-200 opacity-80" : "bg-white border-slate-100"}`}
              >
                <div className="flex items-center gap-1.5">
                  <span className="w-6 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                  {row.product_missing ? (
                    <div className="flex-1 min-w-0 grid grid-cols-[1fr_84px] gap-1.5">
                      <Input
                        data-testid={`quote-product-name-${row.index}`}
                        value={row.product_input}
                        onChange={(e) => update(i, "product_input", e.target.value)}
                        placeholder="Product Name (optional)"
                        disabled={row.locked}
                        className="h-10 rounded-lg text-sm min-w-0"
                      />
                      <Input
                        data-testid={`quote-serial-no-${row.index}`}
                        value={row.serial_input || ""}
                        onChange={(e) => update(i, "serial_input", e.target.value)}
                        placeholder="Serial No."
                        disabled={row.locked}
                        inputMode="numeric"
                        className="h-10 rounded-lg text-sm text-center tabular-nums min-w-0"
                      />
                    </div>
                  ) : (
                    <>
                      {row.editing_product ? (
                        <Input
                          data-testid={`quote-product-edit-${row.index}`}
                          autoFocus
                          value={row.product_input || row.product}
                          onChange={(e) => update(i, "product_input", e.target.value)}
                          onBlur={() => update(i, "editing_product", false)}
                          placeholder="Edit item description"
                          className="h-10 rounded-lg flex-1 min-w-0 text-sm"
                        />
                      ) : (
                        <span className="flex-1 min-w-0 truncate text-sm font-semibold text-slate-900" title={row.product_input || row.product}>
                          {row.product_input || row.product}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => update(i, "editing_product", !row.editing_product)}
                        data-testid={`quote-product-pencil-${row.index}`}
                        className="w-9 h-10 shrink-0 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600 grid place-items-center transition"
                        aria-label="Edit description"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {row.locked ? (
                    <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 uppercase tracking-wider">Locked</span>
                  ) : row.already_quoted ? (
                    <span className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-wider">Revising</span>
                  ) : null}
                </div>
                {row.product_missing && (
                  <p className="text-[10px] text-slate-500 -mt-1">
                    Enter either the <b>Product Name</b> or the <b>Serial No.</b> shown in the image.
                  </p>
                )}
                <div className="grid grid-cols-3 gap-1.5 pt-0.5">
                  <Input
                    data-testid={`quote-unit-price-${row.index}`}
                    type="number"
                    inputMode="decimal"
                    value={row.unit_price}
                    onChange={(e) => update(i, "unit_price", e.target.value)}
                    placeholder="Rate"
                    disabled={row.locked}
                    className="no-spinner h-10 rounded-lg text-sm text-right tabular-nums"
                  />
                  <div className="relative">
                    <Input
                      data-testid={`quote-discount-${row.index}`}
                      type="number" min="0" max="100"
                      value={row.discount}
                      onChange={(e) => update(i, "discount", e.target.value)}
                      placeholder="Discount %"
                      disabled={row.locked}
                      className={`no-spinner h-10 rounded-lg text-sm text-right tabular-nums ${row.discount !== "" ? "pr-6" : ""}`}
                    />
                    {row.discount !== "" && (
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">%</span>
                    )}
                  </div>
                  <Input
                    data-testid={`quote-gst-${row.index}`}
                    type="number" min="0" max="100"
                    value={row.gst || ""}
                    onChange={(e) => update(i, "gst", e.target.value)}
                    placeholder="GST %"
                    disabled={row.locked}
                    className="h-10 rounded-lg text-sm text-right tabular-nums"
                  />
                </div>
                {!row.note_open && !row.note ? (
                  <button
                    type="button"
                    onClick={() => update(i, "note_open", true)}
                    data-testid={`quote-add-note-${row.index}`}
                    className="text-[11px] font-semibold text-emerald-700 hover:underline flex items-center gap-1 pt-0.5"
                  >
                    <Plus className="w-3 h-3" /> Add note
                  </button>
                ) : (
                  <Input
                    data-testid={`quote-note-${row.index}`}
                    value={row.note}
                    onChange={(e) => update(i, "note", e.target.value)}
                    placeholder="Note (e.g. Same-day delivery)"
                    disabled={row.locked}
                    className="h-9 rounded-lg text-xs"
                  />
                )}
                <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                  <span data-testid={`quote-line-formula-${row.index}`} className="text-[10px] text-slate-500">
                    (₹{unit.toFixed(2)}{discPct > 0 ? ` − ${discPct}%` : ""}) × {qty} {row.unit}
                  </span>
                  <span data-testid={`quote-line-total-${row.index}`} className="text-sm font-bold text-slate-900">
                    ₹{lineTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            );
          })}

          {/* #9 — Image enquiries: let the responder add extra rows */}
          {enq?.image_data && (
            <SecondaryButton
              onClick={addImageRow}
              data-testid="quote-add-item"
              className="w-full flex items-center justify-center gap-1"
              disabled={rows.every((r) => r.locked)}
            >
              <Plus className="w-4 h-4" /> Add Another Item
            </SecondaryButton>
          )}
        </div>
  );

  const totalsBlock = (
    <div data-testid="quote-totals" className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-slate-900">Grand Total</span>
        <span className="text-lg font-bold text-emerald-700">₹{Number(totals.grand || 0).toFixed(2)}</span>
      </div>
    </div>
  );

  const actionsBlock = rows.every((r) => r.locked) ? (
    <div data-testid="all-locked-banner" className="bg-slate-100 border border-slate-200 rounded-2xl p-4 text-sm text-slate-700">
      All items in this enquiry have been converted to orders. Quotation is now locked and cannot be revised.
    </div>
  ) : (
    <>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <SecondaryButton
          onClick={() => send(true)}
          disabled={submitting}
          data-testid="quote-wa-btn"
          className="flex items-center justify-center gap-2"
        >
          <MessageCircle className="w-4 h-4" /> Via WhatsApp
        </SecondaryButton>
        <PrimaryButton
          onClick={() => send(false)}
          loading={submitting}
          data-testid="quote-send-btn"
          className="flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          {rows.some((r) => r.already_quoted) ? "Revise Quotation" : "Send Quotation"}
        </PrimaryButton>
      </div>
      {rows.some((r) => r.already_quoted) && (
        <button
          type="button"
          onClick={deleteMyQuotes}
          data-testid="quote-delete-btn"
          className="w-full text-xs font-semibold text-red-600 hover:text-red-700 pt-2"
        >
          Delete this quotation
        </button>
      )}
    </>
  );

  // Split layout: fixed-height 50/50 (top/bottom on mobile, left/right on desktop)
  // when the buyer attached images/PDF pages. Independent scrolling per pane.
  if (hasBuyerImages) {
    return (
      <div
        data-testid="quotation-split-layout"
        className="h-[100dvh] flex flex-col pb-[68px] lg:pb-0 bg-slate-50"
      >
        <Header />
        <div className="px-4 pt-3 pb-2 space-y-2 shrink-0 bg-white/95 backdrop-blur border-b border-slate-100">
          {backButton}
          {businessCard}
          {retentionPill}
        </div>
        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <div
            data-testid="quotation-image-half"
            className="h-1/2 md:h-full md:w-1/2 min-h-0 border-b md:border-b-0 md:border-r border-slate-200 bg-slate-900"
          >
            <QuoteImagePane images={enq.images} testid="quotation-split-image-pane" />
          </div>
          <div
            data-testid="quotation-rate-half"
            className="h-1/2 md:h-full md:w-1/2 min-h-0 overflow-y-auto bg-slate-50 p-3 space-y-3"
          >
            {itemsBlock}
            {totalsBlock}
            {actionsBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        {backButton}
        {businessCard}
        {retentionPill}

        {/* Attached enquiry image from buyer (single-image legacy fallback) */}
        {enq.image_data && (
          <ImageViewer src={enq.image_data} label="Buyer's attached image" testid="inbound-enq-image-viewer" />
        )}

        {itemsBlock}
        {totalsBlock}
        {actionsBlock}
      </div>
    </>
  );
}
